import { useAxios } from '@healthblocks-io/core'
import { useCallback } from 'react'
import { atom, useRecoilState, useResetRecoilState } from 'recoil'

import { localStorageEffect } from '@/capacitor/recoil/localstorage-effect'
import { useLeanSubject } from './useLeanSubject'

import type { NotificationChannel } from '../types/notification-channel.type'

const notificationChannelState = atom<NotificationChannel | undefined>({
  key: '@healthblocks/notification-channel',
  default: undefined,
  effects: [localStorageEffect('@healthblocks/notification-channel')],
})

function useNotificationChannel() {
  const { subject } = useLeanSubject()
  const [notificationChannel, setNotificationChannel] = useRecoilState(notificationChannelState)
  const resetNotificationChannel = useResetRecoilState(notificationChannelState)
  const axios = useAxios()

  const getNotificationChannels = useCallback(() => {
    return axios.get<NotificationChannel[]>('/notification-channel', {
      params: {
        memberId: subject?.id,
      },
    })
  }, [subject, axios])

  const getNotificationChannel = useCallback(
    (id: number) => {
      return axios.get<NotificationChannel>(`notification-channel/${id}`, {
        params: {
          memberId: subject?.id,
        },
      })
    },
    [subject, axios],
  )

  const createNotificationChannel = useCallback(
    (display: string, token: string) => {
      return axios.post<NotificationChannel>('/notification-channel', {
        display,
        token,
        medium: 'firebase',
        memberId: subject?.id,
      })
    },
    [subject, axios],
  )

  const createNotificationChannelIfNotExists = useCallback(
    async (display: string, token: string) => {
      // Don't do network requests if we already have a notification channel
      if (notificationChannel && token === notificationChannel.token) {
        return {
          data: notificationChannel,
        }
      }

      const notificationChannels = await getNotificationChannels()

      const remoteNotificationChannel = notificationChannels?.data.find((channel) => channel.token === token)

      const result = remoteNotificationChannel
        ? { data: remoteNotificationChannel }
        : await createNotificationChannel(display, token)

      setNotificationChannel(result.data)

      return result
    },
    [getNotificationChannels, createNotificationChannel, notificationChannel, setNotificationChannel],
  )

  const deleteNotificationChannel = useCallback(
    (id: number) => {
      return axios.delete<NotificationChannel>(`notification-channel/${id}`)
    },
    [axios],
  )

  const deleteLocalNotificationChannel = useCallback(async () => {
    if (notificationChannel) {
      // Forget the channel locally even if the server delete fails, so the
      // next login registers its own channel instead of reusing this one
      try {
        await deleteNotificationChannel(notificationChannel.id)
      } finally {
        resetNotificationChannel()
      }
    }
  }, [notificationChannel, deleteNotificationChannel, resetNotificationChannel])

  return {
    notificationChannel,
    createNotificationChannelIfNotExists,
    deleteLocalNotificationChannel,
  }
}

export { useNotificationChannel }
